import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Handle, Position } from "@xyflow/react";
import { ChevronDown, ChevronUp, MoreHorizontal, Tag } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import AddTagsDialog from "../../dialog/tags-dialog";
import PlatformBadge from "../common/platform-badge";
import ServiceTypePill from "../common/service-type-pill";
import TroubleshootingInfo from "../common/troubleshooting-info";
import CollapsibleTagList from "./collapsible-tag-list";
import EndpointToggle from "./endpoint-toggle";
import EndpointDetailModal from "../endpoint-detail/endpoint-detail-modal";
import useOverviewStore from "../../../routes/overview/store/overview";
const CustomCardNode = ({ data, setNodeDimensions = () => {} }) => {
  const cardRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 150, height: 50 });
  const [open, setOpen] = useState(false);
  const [endpointModalOpen, setEndpointModalOpen] = useState(false);
  const endpointData = useOverviewStore((state) => state.endpointsByService[data.name || data.title]);

  useEffect(() => {
    if (cardRef.current) {
      const { width, height } = cardRef.current.getBoundingClientRect();
      setDimensions({ width, height });
      setNodeDimensions(data.title, { width, height });
    }
  }, [cardRef, data, setNodeDimensions]);

  return (
    <>
      <Card ref={cardRef} className="border rounded-t-lg rounded-b-none">
        <CardHeader className="border-0">
          <ServiceTypePill type={data.type} />

          <CardTitle>{data.title || "Unknown Service"}</CardTitle>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-lg bg-transparent ml-auto focus:outline-none"
              >
                <MoreHorizontal className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-background">
              <DropdownMenuItem>
                <Button
                  variant="borderless"
                  size="xs"
                  className="w-full text-secondary flex items-center justify-start hover:text-white"
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setOpen(true);
                  }}
                >
                  <Tag size={16} />
                  Edit Tags
                </Button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col justify-start mr-10">
              <span className="text-xs text-gray-400">Platform</span>
              <PlatformBadge platform={data.platform} />
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-gray-400">Connections</span>
              <div className="flex items-center">
                <ChevronUp className="text-green-500 w-4 h-4" />
                <span className="text-sm ml-1">{data.inbound}</span>
                <ChevronDown className="text-yellow-500 w-4 h-4 ml-4" />
                <span className="text-sm ml-1">{data.outbound}</span>
              </div>
            </div>

            <div className="flex flex-col justify-start mr-10">
              <span className="text-xs text-gray-400">Version</span>
              <span className="block text-sm">{data.version || "0.2.1"}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-gray-400">Troubleshooting</span>
              <TroubleshootingInfo {...data} />
            </div>
          </div>
        </CardContent>
        <EndpointToggle
          serviceName={data.name || data.title}
          serviceType={data.type}
          onViewAll={() => setEndpointModalOpen(true)}
        />
      </Card>

      <div className="border-b border-l border-r bg-[#15171C] rounded-b-sm">
        <div className="flex items-center h-full p-2">
          <CollapsibleTagList
            service={data.name}
            tags={data?.tags?.sort((a, b) => a.length - b.length) || []}
            availableWidth={dimensions.width - 32}
          />
        </div>
      </div>
      <AddTagsDialog
        service={data.name || data.title}
        tags={data.tags || []}
        open={open}
        setOpen={setOpen}
        withTrigger={false}
      />
      <EndpointDetailModal
        open={endpointModalOpen}
        onOpenChange={setEndpointModalOpen}
        serviceName={data.name || data.title}
        data={endpointData}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="w-2 h-2 bg-gray-400"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="w-2 h-2 bg-gray-400"
      />
    </>
  );
};

export default CustomCardNode;
